// DynamicExamination.tsx
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
interface TemplateField {
  fieldCode: string;
  label: string;
  dataType: "SingleSelect" | "Text" | "Number" | "LongText";
  options?: string[];
  min?: number;
  max?: number;
}

interface TemplateSection {
  sectionCode: string;
  sectionName: string;
  type: string;
  fields: TemplateField[];
}

interface TemplateJson {
  templateCode: string;
  specialization: string;
  category: string;
  sections: TemplateSection[];
}

interface TemplateApiItem {
  TemplateId: number;
  TemplateCode: string;
  TemplateName: string;
  TemplateVersion: number;
  TemplateCategory: string;
  IsDefault: boolean;
  TemplateJson: string;
}

interface TemplateApiResponse {
  templates: TemplateApiItem[];
}

export interface DynamicExaminationProps {
  apiResponse: TemplateApiResponse;
  initialValues?: Record<string, string>;
  onChange?: (values: Record<string, string>) => void;
  onApply?: (values: Record<string, string>) => void;
  onClear?: () => void;
  showFooter?: boolean;
  columns?: number;
}

// ─────────────────────────────────────────────
// Default columns per sectionCode
// ─────────────────────────────────────────────
const SECTION_COLUMNS: Record<string, number> = {
  GPE: 3,
  LOCAL_EXAM: 3,
  ROM: 3,
  SPECIAL_TESTS: 2,
  NEURO_LIMB: 2,
  ORTHO_SCORES: 3,
};

// ─────────────────────────────────────────────
// Select Component
// ─────────────────────────────────────────────
interface SelectProps {
  options?: any[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  id?: any;
  error?: string | boolean;
}

const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      options = [],
      value,
      onChange,
      className = "",
      disabled = false,
      placeholder = "Select",
      isLoading = false,
      id = "",
      error,
    },
    ref
  ) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(-1);
    const [editing, setEditing] = useState(false);
    const hasError = Boolean(error && String(error).trim() !== "");

    const [menuPos, setMenuPos] = useState({
      left: 0,
      top: 0,
      width: 0,
      flip: false as boolean,
    });

    const normalizedOptions = useMemo(
      () => (Array.isArray(options) ? options : []),
      [options]
    );

    const getValue = (op: any, index: number) =>
      op?.value ?? op?.id ?? op ?? index;
    const getLabel = (op: any) => op?.label ?? op?.name ?? String(op ?? "");

    const selectedOption = useMemo(() => {
      const v = value ?? "";
      return (
        normalizedOptions.find(
          (op, i) => String(getValue(op, i)) === String(v)
        ) ?? null
      );
    }, [normalizedOptions, value]);

    const selectedLabel = selectedOption ? getLabel(selectedOption) : "";

    const filtered = useMemo(() => {
      const q = query.trim().toLowerCase();
      if (!q) return normalizedOptions;
      return normalizedOptions.filter((op) =>
        getLabel(op).toLowerCase().includes(q)
      );
    }, [normalizedOptions, query]);

    const isDisabled = disabled || isLoading;

    const measureMaxOptionWidth = (
      labels: string[],
      sampleEl: HTMLElement | null
    ) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return 0;
      const font = sampleEl
        ? window.getComputedStyle(sampleEl).font
        : "14px sans-serif";
      ctx.font = font;
      let max = 0;
      for (const t of labels) {
        const w = ctx.measureText(t ?? "").width;
        if (w > max) max = w;
      }
      return max;
    };

    const getActualMenuHeight = () => {
      const maxH = 220;
      const el = menuRef.current;
      if (!el) return maxH;
      return Math.min(el.scrollHeight, maxH);
    };

    const updateMenuPos = () => {
      const el = inputRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const anchorWidth = rect.width;
      const labels = normalizedOptions.map((op) => getLabel(op));
      const maxLabelW = measureMaxOptionWidth(labels, el);
      const extra = 48;
      const desiredWidth = Math.ceil(maxLabelW + extra);
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const actualMenuHeight = getActualMenuHeight();
      const gap = 4;
      let flip = false;
      let topPos = rect.bottom + gap;
      if (spaceBelow < actualMenuHeight && spaceAbove > spaceBelow) {
        flip = true;
        topPos = rect.top - gap - actualMenuHeight;
        if (topPos < gap) topPos = gap;
      }
      setMenuPos({
        left: rect.left,
        top: topPos,
        width: Math.max(anchorWidth, desiredWidth),
        flip,
      });
    };

    const close = () => {
      setOpen(false);
      setQuery("");
      setActiveIndex(-1);
      setEditing(false);
    };

    const openAndFocus = () => {
      if (isDisabled) return;
      setOpen(true);
      setEditing(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    };

    useEffect(() => {
      const onDocMouseDown = (e: MouseEvent) => {
        if (!rootRef.current) return;
        if (!rootRef.current.contains(e.target as Node)) close();
      };
      document.addEventListener("mousedown", onDocMouseDown);
      return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, []);

    useEffect(() => {
      if (!open) return;
      updateMenuPos();
      const raf = requestAnimationFrame(() => updateMenuPos());
      const onReposition = () => updateMenuPos();
      window.addEventListener("resize", onReposition);
      window.addEventListener("scroll", onReposition, true);
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", onReposition);
        window.removeEventListener("scroll", onReposition, true);
      };
    }, [open, normalizedOptions, query]);

    useEffect(() => {
      if (!open) return;
      const selectedIdxInFiltered = filtered.findIndex((op, i) => {
        const ov = getValue(op, i);
        return String(ov) === String(value ?? "");
      });
      if (selectedIdxInFiltered >= 0) setActiveIndex(selectedIdxInFiltered);
      else if (filtered.length) setActiveIndex(0);
      else setActiveIndex(-1);
    }, [open, value, query, filtered]);

    const commit = (op: any) => {
      const idx = normalizedOptions.indexOf(op);
      const v = getValue(op, idx >= 0 ? idx : 0);
      onChange?.(v);
      close();
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (!open) setOpen(true);
        if (filtered.length)
          setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (!open) setOpen(true);
        if (filtered.length) setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        if (!open) return;
        e.preventDefault();
        const op = activeIndex >= 0 ? filtered[activeIndex] : null;
        if (op) commit(op);
      } else if (e.key === "Escape") {
        e.preventDefault();
        close();
      } else if (e.key === "Tab") {
        close();
      }
    };

    return (
      <div
        ref={(el) => {
          rootRef.current = el;
          if (typeof ref === "function") ref(el);
          else if (ref) ref.current = el;
        }}
        id={id}
        className={`relative ${className}`}
      >
        <div
          className={`border rounded px-1 py-1 text-xs
            ${hasError ? "border-red-500 bg-red-50" : "border-[#E5E7EB] bg-[#ebebeb82]"}
            ${isDisabled ? "bg-gray-100 cursor-not-allowed" : "cursor-text"}
          `}
          onMouseDown={(e) => {
            if (isDisabled) return;
            e.preventDefault();
            if (!open) openAndFocus();
            else inputRef.current?.focus();
          }}
          title={selectedLabel}
        >
          <div style={{ position: "relative" }}>
            <input
              ref={inputRef}
              disabled={isDisabled}
              value={open ? (editing ? query : selectedLabel) : selectedLabel}
              onChange={(e) => {
                setEditing(true);
                setQuery(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => {
                if (isDisabled) return;
                setOpen(true);
                setEditing(false);
              }}
              onKeyDown={onKeyDown}
              placeholder={isLoading ? "Loading..." : placeholder}
              className="w-full bg-transparent outline-none text-sm"
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls="select-listbox"
              role="combobox"
              style={{
                paddingRight: "18px",
                cursor: isDisabled ? "not-allowed" : "pointer",
              }}
            />
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                right: "4px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                display: "inline-flex",
                alignItems: "center",
                color: "#6B7280",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </div>
        </div>

        {open && !isDisabled && (
          <div
            ref={menuRef}
            id="select-listbox"
            role="listbox"
            className="z-50 overflow-hidden rounded border border-[#E5E7EB] bg-white shadow-sm"
            style={{
              position: "fixed",
              left: menuPos.left,
              top: menuPos.top,
              width: menuPos.width,
              maxHeight: "220px",
              overflowY: "auto",
            }}
          >
            {filtered.length === 0 ? (
              <div className="px-2 py-2 text-xs text-gray-500">No results</div>
            ) : (
              filtered.map((op, i) => {
                const optionValue = getValue(op, i);
                const label = getLabel(op);
                const isSelected = String(optionValue) === String(value ?? "");
                const isActive = i === activeIndex;
                return (
                  <div
                    key={String(optionValue) + "-" + i}
                    role="option"
                    aria-selected={isSelected}
                    className={`px-2 py-1 text-xs cursor-pointer ${
                      isSelected || isActive
                        ? "bg-[#2f6f63] text-white"
                        : "bg-white text-[#84898B]"
                    }`}
                    onMouseEnter={() => setActiveIndex(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commit(op);
                    }}
                  >
                    {label}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    );
  }
);

// ─────────────────────────────────────────────
// InputField Component
// ─────────────────────────────────────────────
interface InputFieldProps {
  value?: string;
  id?: string;
  onChange?: (value: string) => void;
  className?: string;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  inputMode?: any;
  pattern?: string;
  error?: string;
  min?: number;
  max?: number;
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      value,
      id = "",
      onChange,
      className = "",
      type = "text",
      placeholder = "",
      disabled = false,
      inputMode = "text",
      pattern = "",
      error,
      min,
      max,
    },
    ref
  ) => {
    const hasError = error && error.trim() !== "";

    return (
      <input
        ref={ref}
        id={id}
        type={type}
        inputMode={inputMode}
        pattern={pattern}
        min={min}
        max={max}
        className={`
          rounded px-1 py-1 text-sm bg-[#ebebeb82] w-full border
          ${hasError ? "border-red-500" : "border-[#E5E7EB]"}
          focus:outline-none
          ${className}
        `}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        title={value}
      />
    );
  }
);

// ─────────────────────────────────────────────
// Field Renderer — driven by dataType from JSON
// ─────────────────────────────────────────────
const renderDynamicField = (
  field: TemplateField,
  value: string | undefined,
  onChange: (code: string, value: string) => void
) => {
  switch (field.dataType) {
    case "SingleSelect":
      return (
        <Select
          options={(field.options || []).map((opt) => ({
            label: opt,
            value: opt,
          }))}
          value={value ?? ""}
          onChange={(v) => onChange(field.fieldCode, String(v))}
          placeholder="Select"
        />
      );

    case "Text":
      return (
        <InputField
          id={field.fieldCode}
          value={value ?? ""}
          onChange={(v) => onChange(field.fieldCode, v)}
          placeholder={field.label}
          error=""
        />
      );

    case "Number":
      return (
        <InputField
          id={field.fieldCode}
          type="number"
          inputMode="numeric"
          value={value ?? ""}
          onChange={(v) => onChange(field.fieldCode, v)}
          placeholder={field.label}
          min={field.min}
          max={field.max}
          error=""
        />
      );

    case "LongText":
      return (
        <textarea
          id={field.fieldCode}
          value={value ?? ""}
          onChange={(e) => onChange(field.fieldCode, e.target.value)}
          placeholder={field.label}
          rows={3}
          className="w-full border border-[#E5E7EB] rounded px-2 py-1 text-sm bg-[#ebebeb82] focus:outline-none resize-y"
        />
      );

    default:
      return (
        <InputField
          id={field.fieldCode}
          value={value ?? ""}
          onChange={(v) => onChange(field.fieldCode, v)}
          placeholder={field.label}
          error=""
        />
      );
  }
};

// ─────────────────────────────────────────────
// Main DynamicExamination Component
// ─────────────────────────────────────────────
const DynamicExamination: React.FC<DynamicExaminationProps> = ({
  apiResponse,
  initialValues = {},
  onChange,
  onApply,
  onClear,
  showFooter = true,
  columns, // global override — if omitted, falls back to SECTION_COLUMNS per section
}) => {
  const [formData, setFormData] = useState<Record<string, string>>(initialValues);

  useEffect(() => {
    setFormData(initialValues);
  }, [JSON.stringify(initialValues)]);

  const parsedTemplate = useMemo<TemplateJson | null>(() => {
    try {
      const templateItem = apiResponse?.templates?.[0];
      if (!templateItem?.TemplateJson) return null;
      return JSON.parse(templateItem.TemplateJson) as TemplateJson;
    } catch {
      return null;
    }
  }, [apiResponse]);

  const handleChange = (fieldCode: string, value: string) => {
    const updated = { ...formData, [fieldCode]: value };
    setFormData(updated);
    onChange?.(updated);
  };

  const handleClear = () => {
    setFormData({});
    onChange?.({});
    onClear?.();
  };

  const handleApply = () => {
    onApply?.(formData);
  };

  if (!parsedTemplate) {
    return (
      <div className="p-4 text-sm text-red-500">
        Failed to parse template JSON.
      </div>
    );
  }

  return (
    <div className="bg-white font-sans text-sm text-gray-800 w-full">
      {/* Header */}
      <div className="border-b-2 border-blue-500 mb-5 pb-1 px-4 pt-4">
        <h1 className="text-base font-semibold">
          <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded-sm mr-1">
            {parsedTemplate.specialization}
          </span>
          <span className="text-blue-600"> {parsedTemplate.category}</span>
        </h1>
      </div>

      <div className="px-4 pb-6 space-y-5">
        {parsedTemplate.sections.map((section) => {
          // ✅ FIX: resolve cols inside the map using the section's own code
          const cols = columns ?? SECTION_COLUMNS[section.sectionCode] ?? 3;

          return (
            <div
              key={section.sectionCode}
              className="border border-gray-200 rounded-md p-4 bg-gray-50"
            >
              {/* Section Header */}
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <span className="font-semibold text-sm text-gray-700">
                  {section.sectionName}
                </span>
              </div>

              {/* Fields Grid */}
              <div
                className="grid gap-x-4 gap-y-4"
                style={{
                  // ✅ FIX: cols is now properly resolved before use here
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                }}
              >
                {section.fields.map((field) => (
                  <div
                    key={field.fieldCode}
                    style={
                      // ✅ FIX: LongText always spans full width via inline style
                      // (Tailwind dynamic col-span-N classes are not safe at runtime)
                      field.dataType === "LongText"
                        ? { gridColumn: "1 / -1" }
                        : {}
                    }
                  >
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {field.label}
                    </label>
                    {renderDynamicField(
                      field,
                      formData[field.fieldCode],
                      handleChange
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Footer Buttons */}
        {showFooter && (
          <div className="flex justify-end gap-3 pt-2">
            <button
              className="border border-gray-300 text-gray-700 px-4 py-1.5 rounded-sm text-sm hover:bg-gray-50"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              className="text-white px-4 py-1.5 rounded-sm text-sm bg-[#128273] hover:bg-[#0f6e61] cursor-pointer"
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicExamination;
